// ============================================
// P1.5 G-02：组件预览样例构造（纯函数，供 WidgetPreviewCard 渲染实时小预览）
// 属性继承：按四层 schema 默认值生成 config（与真实拖入行为一致）
// ============================================
import type { VisualComponent } from '@dtwin/shared';
import { getWidgetSchema } from '../../registry/widgetSchema';
import { getStaticExampleFor } from './widgetDataGuides';

/** 由 schema 默认值生成组件配置（base/regular/subgroup/special 四层默认值全在 schema 内） */
export function defaultConfigOf(type: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of getWidgetSchema(type)) {
    if (f.default !== undefined) out[f.field] = f.default;
  }
  return out;
}

/** 由数据引导示例生成静态数据（解析失败回退空数组） */
export function previewDataOf(type: string): unknown[] {
  try {
    const parsed = JSON.parse(getStaticExampleFor(type));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// ============================================
// 媒体类预览演示配置（2026-10-14）：媒体组件实时预览需要样例素材，
// 否则卡片只会显示"点击配置 URL"占位文案（等价于没有预览图）。
// 图片用内联 SVG data URL（零网络）；视频/视频流用本地演示 mp4；网页嵌入用本地示例页。
// ============================================
const svgDemo = (text: string, from: string, to: string) =>
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="180"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/></linearGradient></defs><rect width="320" height="180" fill="url(#g)"/><text x="160" y="98" fill="#ffffff" font-size="18" font-family="sans-serif" text-anchor="middle">${text}</text></svg>`,
  );
const DEMO_IMAGE_1 = svgDemo('示例图片', '#12304f', '#1890ff');
const DEMO_IMAGE_2 = svgDemo('示例图片 2', '#1d3a2a', '#52c41a');

export const MEDIA_DEMO_CONFIG: Record<string, Record<string, unknown>> = {
  image: { src: DEMO_IMAGE_1 },
  'image-carousel': {
    imagesJson: JSON.stringify([{ url: DEMO_IMAGE_1, title: '示例一' }, { url: DEMO_IMAGE_2, title: '示例二' }]),
  },
  video: { src: '/assets/demo/sample.mp4', poster: DEMO_IMAGE_1 },
  'stream-video': { url: '/assets/demo/sample.mp4' },
  'iframe-web': { url: '/assets/demo/iframe-sample.html' },
};

export interface PreviewComponentOptions {
  width?: number;
  height?: number;
}

/** 构造预览用样例组件（不出现在 store，仅渲染盒消费） */
export function buildPreviewComponent(
  type: string,
  options: PreviewComponentOptions = {},
): VisualComponent {
  const width = options.width ?? 400;
  const height = options.height ?? 300;
  return {
    id: `preview-${type}`,
    type,
    pageId: 'preview',
    position: { x: 0, y: 0 },
    size: { width, height },
    style: {},
    config: { ...defaultConfigOf(type), ...(MEDIA_DEMO_CONFIG[type] || {}) },
    dataSource: {
      type: 'static',
      config: { data: previewDataOf(type) },
    },
  } as unknown as VisualComponent;
}
